'use client';
import React from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { MenuToggleIcon } from '@/components/ui/menu-toggle-icon';
import { createPortal } from 'react-dom';
import {
	NavigationMenu,
	NavigationMenuContent,
	NavigationMenuItem,
	NavigationMenuLink,
	NavigationMenuList,
	NavigationMenuTrigger,
} from '@/components/ui/navigation-menu';
import {
	CodeIcon,
	Users,
	Star,
	FileText,
	Shield,
	RotateCcw,
	Handshake,
	HelpCircle,
	BarChart,
    Compass,
    Palette,
    Bot,
    ShoppingBag,
    Workflow,
    Rocket,
} from 'lucide-react';
import { Logo } from '@/components/Logo';
import { usePathname, useRouter } from 'next/navigation';
import { ShimmerButton } from '../magicui/shimmer-button';
import Link from 'next/link';

export function Header() {
	const [open, setOpen] = React.useState(false);
	const scrolled = useScroll(10);
	const scrollY = useScrollPosition();
    const pathname = usePathname();
    const router = useRouter();

    const isHome = pathname === '/';
    // The homepage stays cinematic through the hero, story reel, and proof bridge.
    const isDarkBackground = isHome && scrollY < 7600;
	const showScrolledHeader = scrolled && !isDarkBackground;

	React.useEffect(() => {
		if (open) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = '';
		}
		return () => {
			document.body.style.overflow = '';
		};
	}, [open]);

	return (
        <>
		<header
			className={cn('fixed top-0 z-50 w-full border-b border-transparent transition-all duration-300', {
				'bg-background/95 supports-[backdrop-filter]:bg-background/50 border-border backdrop-blur-lg':
					showScrolledHeader,
				// At the very top of the homepage, float the nav down so the hero card's
				// top border line has room to breathe; it docks back as you scroll.
				'pt-5': isHome && !scrolled,
			})}
		>
			<nav className="mx-auto flex h-14 w-full max-w-5xl items-center justify-between px-4">
				<div className="flex items-center gap-5">
					<Link
						href="/"
						className={cn(
							"rounded-md p-2 transition-[background-color,box-shadow] duration-200 focus-visible:outline-none focus-visible:ring-2",
							isDarkBackground
								? "hover:bg-neutral-950/25 hover:shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)] focus-visible:bg-neutral-950/30 focus-visible:ring-white/25"
								: "hover:bg-accent focus-visible:bg-accent focus-visible:ring-neutral-950/15"
						)}
					>
                        <Logo invert={isDarkBackground} className={cn("h-8 transition-colors duration-300", isDarkBackground ? "text-white" : "text-neutral-950")} />
					</Link>
					<NavigationMenu className="hidden md:flex">
						<NavigationMenuList>
							<NavigationMenuItem>
								<NavigationMenuTrigger className={cn("bg-transparent", isDarkBackground ? "text-white hover:text-white focus:text-white data-[active]:bg-white/20 data-[state=open]:bg-white/20" : "")}>Solutions</NavigationMenuTrigger>
								<NavigationMenuContent className="bg-background p-1 pr-1.5">
									<ul className="bg-popover grid w-lg grid-cols-2 gap-2 rounded-md border p-2 shadow">
										{solutionsLinks.map((item, i) => (
											<li key={i}>
												<ListItem {...item} />
											</li>
										))}
									</ul>
									<div className="p-2">
										<p className="text-muted-foreground text-sm">
											Interested?{' '}
											<Link href="/pricing" className="text-foreground font-medium hover:underline">
												See what it costs
											</Link>
										</p>
									</div>
								</NavigationMenuContent>
							</NavigationMenuItem>
							<NavigationMenuItem>
								<NavigationMenuTrigger className={cn("bg-transparent", isDarkBackground ? "text-white hover:text-white focus:text-white data-[active]:bg-white/20 data-[state=open]:bg-white/20" : "")}>Company</NavigationMenuTrigger>
								<NavigationMenuContent className="bg-background p-1 pr-1.5 pb-1.5">
									<div className="grid w-lg grid-cols-2 gap-2">
										<ul className="bg-popover space-y-2 rounded-md border p-2 shadow">
											{companyLinks.map((item, i) => (
												<li key={i}>
													<ListItem {...item} />
												</li>
											))}
										</ul>
										<ul className="space-y-2 p-3">
											{companyLinks2.map((item, i) => (
												<li key={i}>
													<NavigationMenuLink
														asChild
														className="flex p-2 hover:bg-accent flex-row rounded-md items-center gap-x-2"
													>
                                                        <Link href={item.href} className="flex flex-row items-center gap-x-2 w-full">
                                                            <item.icon className="text-foreground size-4" />
                                                            <span className="font-medium">{item.title}</span>
                                                        </Link>
													</NavigationMenuLink>
												</li>
											))}
										</ul>
									</div>
								</NavigationMenuContent>
							</NavigationMenuItem>
                            <NavigationMenuLink className="px-4" asChild>
								<Link href="/contact" className={cn("hover:bg-accent rounded-md p-2", isDarkBackground ? "text-white hover:bg-white/20 hover:text-white" : "")}>
									Contact
								</Link>
							</NavigationMenuLink>
							<NavigationMenuLink className="px-4" asChild>
								<Link href="/pricing" className={cn("hover:bg-accent rounded-md p-2", isDarkBackground ? "text-white hover:bg-white/20 hover:text-white" : "")}>
									Pricing
								</Link>
							</NavigationMenuLink>
						</NavigationMenuList>
					</NavigationMenu>
				</div>
				<div className="hidden items-center gap-2 md:flex">
					{/* The audit CTA leads to the Revenue Code Diagnostic — the lead magnet. */}
					<ShimmerButton className="font-montserrat p-3 py-2 tracking-wide text-sm" onClick={() => router.push('/diagnostic')}>Get Your Free Audit</ShimmerButton>
				</div>
				<Button
					size="icon"
					variant="outline"
					onClick={() => setOpen(!open)}
					className="md:hidden"
					aria-expanded={open}
					aria-controls="mobile-menu"
					aria-label="Toggle menu"
				>
					<MenuToggleIcon open={open} className="size-5" duration={300} />
				</Button>
			</nav>
			<MobileMenu open={open} className="flex flex-col justify-between gap-4 overflow-y-auto">
				<NavigationMenu className="max-w-full">
					<div className="flex w-full flex-col gap-y-3">
						<MobileSectionHeading
							title="Solutions"
							href="/solutions"
							isActive={isRouteActive(pathname, '/solutions')}
							onClick={() => setOpen(false)}
						/>
						<div className="grid gap-2">
						{solutionsLinks.map((link) => (
							<ListItem key={link.title} {...link} isMobile onClick={() => setOpen(false)} />
						))}
						</div>
                        <div className="h-px bg-gradient-to-r from-transparent via-neutral-200 to-transparent my-1" />
						<MobileSectionHeading title="Company" />
						<div className="grid gap-2">
						{companyLinks.map((link) => (
							<ListItem
								key={link.title}
								{...link}
								isActive={isRouteActive(pathname, link.href)}
								isMobile
								onClick={() => setOpen(false)}
							/>
						))}
						{companyLinks2.map((link) => (
							<ListItem
								key={link.title}
								{...link}
								isActive={isRouteActive(pathname, link.href) && !['Refund Policy', 'Help Center'].includes(link.title)}
								isMobile
								onClick={() => setOpen(false)}
							/>
						))}
						</div>
                        <div className="h-px bg-gradient-to-r from-transparent via-neutral-200 to-transparent my-1" />
                        <Link
							href="/pricing"
							aria-current={isRouteActive(pathname, '/pricing') ? 'page' : undefined}
							onClick={() => setOpen(false)}
							className={cn(
								"group relative flex items-center justify-between overflow-hidden rounded-xl border p-3.5 transition-all duration-200",
								isRouteActive(pathname, '/pricing')
									? "border-neutral-950 bg-neutral-950 text-white shadow-lg shadow-neutral-950/15"
									: "border-neutral-200 bg-white/80 text-neutral-950 shadow-sm shadow-neutral-950/5 hover:border-neutral-300 hover:bg-white"
							)}
						>
                            <span className="font-semibold tracking-[-0.01em]">Pricing</span>
							{isRouteActive(pathname, '/pricing') && (
								<span className="rounded-full border border-white/20 bg-white/10 px-2 py-0.5 text-[0.625rem] font-semibold uppercase tracking-[0.16em] text-white/85">
									Current
								</span>
							)}
                        </Link>
                        <Link
							href="/contact"
							aria-current={isRouteActive(pathname, '/contact') ? 'page' : undefined}
							onClick={() => setOpen(false)}
							className={cn(
								"group relative flex items-center justify-between overflow-hidden rounded-xl border p-3.5 transition-all duration-200",
								isRouteActive(pathname, '/contact')
									? "border-neutral-950 bg-neutral-950 text-white shadow-lg shadow-neutral-950/15"
									: "border-neutral-200 bg-white/80 text-neutral-950 shadow-sm shadow-neutral-950/5 hover:border-neutral-300 hover:bg-white"
							)}
						>
                            <span className="font-semibold tracking-[-0.01em]">Contact</span>
							{isRouteActive(pathname, '/contact') && (
								<span className="rounded-full border border-white/20 bg-white/10 px-2 py-0.5 text-[0.625rem] font-semibold uppercase tracking-[0.16em] text-white/85">
									Current
								</span>
							)}
                        </Link>
					</div>
				</NavigationMenu>
				<div className="flex flex-col gap-2">
					<ShimmerButton className="w-full " onClick={() => {
                        setOpen(false);
                        router.push('/diagnostic');
                    }}>Get Your Free Audit</ShimmerButton>
				</div>
			</MobileMenu>
		</header>
        </>
	);
}

function MobileMenu({ open, children, className, ...props }) {
	if (!open || typeof window === 'undefined') return null;

	return createPortal(
		<div
			id="mobile-menu"
			className={cn(
				'bg-white/95 supports-[backdrop-filter]:bg-white/85 backdrop-blur-2xl',
				'fixed top-14 right-0 bottom-0 left-0 z-40 flex flex-col overflow-hidden border-y border-neutral-200/70 shadow-2xl shadow-neutral-950/10 md:hidden',
			)}
		>
			<div
				data-slot={open ? 'open' : 'closed'}
				className={cn(
					'data-[slot=open]:animate-in data-[slot=open]:zoom-in-97 ease-out',
					'size-full p-4',
					className,
				)}
				{...props}
			>
				{children}
			</div>
		</div>,
		document.body,
	);
}

function MobileSectionHeading({ title, href, isActive, onClick }) {
	const content = (
		<>
			<span className="text-[0.68rem] font-semibold uppercase tracking-[0.18em] text-neutral-500">
				{title}
			</span>
			{isActive && (
				<span className="rounded-full bg-neutral-950 px-2 py-0.5 text-[0.625rem] font-semibold uppercase tracking-[0.16em] text-white">
					Current
				</span>
			)}
		</>
	);

	const className = cn(
		"flex items-center justify-between px-1",
		href && "rounded-lg py-1 transition-colors hover:bg-neutral-100"
	);

	if (href) {
		return (
			<Link href={href} onClick={onClick} aria-current={isActive ? 'page' : undefined} className={className}>
				{content}
			</Link>
		);
	}

	return <div className={className}>{content}</div>;
}

function ListItem({
	title,
	description,
	icon: Icon,
	className,
	iconClassName,
	iconContainerClassName,
	titleClassName,
	descriptionClassName,
	href,
	isActive = false,
	isMobile = false,
	...props
}) {
	return (
		<NavigationMenuLink
			aria-current={isActive ? 'page' : undefined}
			className={cn(
				'w-full flex flex-row gap-x-2 transition-all duration-200 data-[active=true]:focus:bg-accent data-[active=true]:hover:bg-accent data-[active=true]:bg-accent/50 data-[active=true]:text-accent-foreground hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground rounded-sm p-2',
				isMobile && 'group relative overflow-hidden rounded-xl border border-neutral-200 bg-white/80 p-3 shadow-sm shadow-neutral-950/5 hover:border-neutral-300 hover:bg-white hover:shadow-md hover:shadow-neutral-950/10',
				isMobile && isActive && 'border-neutral-950 bg-neutral-950 text-white shadow-lg shadow-neutral-950/15 hover:bg-neutral-950 hover:text-white',
				className
			)}
			{...props}
			asChild
		>
			<Link href={href}>
				<div
					className={cn(
						"bg-background/40 flex aspect-square size-12 shrink-0 items-center justify-center rounded-md border shadow-sm transition-colors duration-200",
						isMobile && "bg-neutral-50 border-neutral-200",
						isMobile && isActive && "border-white/15 bg-white/10 shadow-none",
						iconContainerClassName
					)}
				>
					<Icon className={cn("text-foreground size-5 transition-colors duration-200", isMobile && "text-neutral-800", isMobile && isActive && "text-white", iconClassName)} />
				</div>
				<div className="flex min-w-0 flex-1 flex-col items-start justify-center">
					<span className={cn("font-medium", isMobile && "text-[0.96rem] font-semibold tracking-[-0.01em] text-neutral-950", isMobile && isActive && "text-white", titleClassName)}>{title}</span>
					{description && (
						<span className={cn("text-muted-foreground text-xs", isMobile && "mt-0.5 text-[0.78rem] leading-5 text-neutral-500", isMobile && isActive && "text-white/70", descriptionClassName)}>{description}</span>
					)}
				</div>
				{isMobile && isActive && (
					<span className="self-start rounded-full border border-white/20 bg-white/10 px-2 py-0.5 text-[0.625rem] font-semibold uppercase tracking-[0.16em] text-white/85">
						Current
					</span>
				)}
			</Link>
		</NavigationMenuLink>
	);
}

function isRouteActive(pathname, href) {
	if (!pathname || !href || href.startsWith('http') || href.startsWith('#')) {
		return false;
	}

	const normalize = (value) => {
		const path = value.split('?')[0].split('#')[0];
		return path.length > 1 ? path.replace(/\/$/, '') : path;
	};

	const current = normalize(pathname);
	const target = normalize(href);

	return target === '/' ? current === '/' : current === target || current.startsWith(`${target}/`);
}

const solutionsLinks = [
	{
		title: 'Zero-to-Hero',
		href: '/zero-to-hero',
		description: 'Founder track from idea to fundable company',
		icon: Rocket,
		className: 'border border-[#C9A84C]/40 bg-neutral-950 text-white hover:border-[#C9A84C] hover:bg-neutral-900 hover:text-white focus:border-[#C9A84C] focus:bg-neutral-900 focus:text-white',
		iconContainerClassName: 'border-[#C9A84C]/30 bg-[#C9A84C]/10 shadow-none',
		iconClassName: 'text-[#C9A84C]',
		titleClassName: 'text-white',
		descriptionClassName: 'text-neutral-400',
	},
	{
		title: 'Product Strategy',
		href: '/solutions',
		description: 'Define your roadmap and vision',
		icon: Compass,
	},
	{
		title: 'Design',
		href: '/solutions',
		description: 'Craft beautiful user experiences',
		icon: Palette,
	},
	{
		title: 'Fullstack Development',
		href: '/solutions',
		description: 'Build robust and scalable applications',
		icon: CodeIcon,
	},
	{
		title: 'AI Systems',
		href: '/solutions',
		description: 'Leverage the power of artificial intelligence',
		icon: Bot,
	},
	{
		title: 'E-Commerce',
		href: '/solutions',
		description: 'Sell your products online',
		icon: ShoppingBag,
	},
	{
		title: 'SEO & Performance',
		href: '/solutions',
		description: 'Optimize for search engines and speed',
		icon: BarChart,
	},
];

const companyLinks = [
	{
		title: 'About',
		href: '/about',
		description: 'Learn more about our story and team',
		icon: Users,
	},
	{
		title: 'Process',
		href: '/process',
		description: 'How we work',
		icon: Workflow,
	},
	{
		title: 'Customer Stories',
		href: '/work',
		description: 'See how we’ve helped our clients succeed',
		icon: Star,
	},
    {
		title: 'Blog',
		href: '/blog',
        description: 'Latest news and insights',
		icon: FileText,
	},
	{
		title: 'Partnerships',
		href: '/partnerships',
		icon: Handshake,
		description: 'Collaborate with us for mutual growth',
	},
];

const companyLinks2 = [
	{
		title: 'Terms of Service',
		href: '/tos',
		icon: FileText,
	},
	{
		title: 'Privacy Policy',
		href: '/privacy-policy',
		icon: Shield,
	},
	{
		title: 'Refund Policy',
		href: '/tos',
		icon: RotateCcw,
	},
	{
		title: 'Help Center',
		href: '/contact',
		icon: HelpCircle,
	},
];


function useScroll(threshold) {
	const [scrolled, setScrolled] = React.useState(false);

	const onScroll = React.useCallback(() => {
		setScrolled(window.scrollY > threshold);
	}, [threshold]);

	React.useEffect(() => {
		window.addEventListener('scroll', onScroll);
		return () => window.removeEventListener('scroll', onScroll);
	}, [onScroll]);

	// also check on first load
	React.useEffect(() => {
		onScroll();
	}, [onScroll]);

	return scrolled;
}

function useScrollPosition() {
	const [scrollY, setScrollY] = React.useState(0);

	const onScroll = React.useCallback(() => {
		setScrollY(window.scrollY);
	}, []);

	React.useEffect(() => {
		onScroll();
		window.addEventListener('scroll', onScroll, { passive: true });
		return () => window.removeEventListener('scroll', onScroll);
	}, [onScroll]);

	return scrollY;
}
